import assign from 'object-assign';

import constants from '../utils/constants.js';

import GenericStore from './GenericStore.js';
import NoteStore from './NoteStore.js';
import PersistentStore from './PersistentStore.js';

const {GAME_LENGTH} = constants;

let currentNote = '';
let cardCount = 0;
let failureCount = 0;
let successes = [];
let failures = [];
let startTime = 0;
let endTime = 0;
let practice = 'name';
let selectedModifier = 'natural';

// Pages:
// PAGE_START --> PAGE_NOTE --> PAGE_SUCCESS --> PAGE_DONE
//                   ^                 |
//                   +-----------------+
//
// Each "game" goes through a number of cards to determine the "score".
// The score is the number of seconds to go through the whole game, with
// penalty seconds added for wrong answers.
//
// The ten most recent best scores (with date and time) are remembered.
//
// GAME_LENGTH is the number of cards per game; set to a low number for testing,
// then will become e.g. 25; in the future may be configurable.
// startTime is the timestamp when the game started (to calculate the
// elapsed time)
// endTime is the timestamp when the game ends
// failureCount is the number of wrong guesses, to be added as penalty seconds
//
// functions:
//  - startGame()
//  - getElapsedTime() -> seconds
//  - getFailureCount() -> number of failures so far
//
// Note: the scores will be stored in the PersistentStore.

const SessionStore = assign({}, GenericStore, {
  PAGES: {
    PAGE_START: 'PAGE_START',
    PAGE_NOTE: 'PAGE_NOTE',
    PAGE_SUCCESS: 'PAGE_SUCCESS',
    PAGE_DONE: 'PAGE_DONE'
  },

  getPage: function() {
    return currentPage;
  },

  setPage: function(page) {
    currentPage = page;
    SessionStore.emitChange();
  },

  backToStart: function() {
    currentPage = SessionStore.PAGES.PAGE_START;
    startTime = 0;
    endTime = 0;
    cardCount = 0;
    failureCount = 0;
    failures = [];
    SessionStore.emitChange();
  },

  startGame: function() {
    currentPage = SessionStore.PAGES.PAGE_NOTE;
    NoteStore.setNewNoteNr();
    startTime = Date.now();
    endTime = 0;
    cardCount = 1;
    failureCount = 0;
    failures = [];
    SessionStore.emitChange();
    setInterval(() => {
      // emit a change every 250 ms to display the time at the top
      SessionStore.emitChange();
    }, 250);
  },

  nextCard: function() {
    if (cardCount >= GAME_LENGTH) {
      // game over
      endTime = Date.now();
      const seconds = Math.floor((endTime - startTime) / 1000);
      PersistentStore.addScore(seconds + failureCount);

      currentPage = SessionStore.PAGES.PAGE_DONE;
    } else {
      cardCount++;
      NoteStore.setNewNoteNr();
      currentPage = SessionStore.PAGES.PAGE_NOTE;
    }

    SessionStore.emitChange();
  },

  getElapsedTime: function() {
    if (startTime === 0) {
      return -1;
    }
    if (endTime > 0) {
      return Math.floor((endTime - startTime) / 1000);
    }
    return Math.floor((Date.now() - startTime) / 1000);
  },

  getCurrentNote: function() {
    return currentNote;
  },

  setCurrentNote: function(note) {
    currentNote = note;
    SessionStore.emitChange();
  },

  getCardCount: function() {
    return cardCount;
  },

  getFailureCount: function() {
    return failureCount;
  },

  setFailureCount: function(count) {
    failureCount = count;
    SessionStore.emitChange();
  },

  recordSuccess: function(note = '') {
    successes[note] = true;
    failures = [];
    SessionStore.nextCard();
    setTimeout(() => {
      successes[note] = false;
      SessionStore.emitChange();
    }, 500);
  },

  recordFailure: function(note) {
    failureCount++;
    failures[note] = true;
    SessionStore.emitChange();
    setTimeout(() => {
      failures[note] = false;
      SessionStore.emitChange();
    }, 500);
  },

  isFailure: function(note) {
    return failures[note];
  },

  isSuccess: function(note) {
    return successes[note];
  },

  clearSuccess: function() {
    currentPage = SessionStore.PAGES.PAGE_NOTE;
    SessionStore.emitChange();
  },

  getPractice: function() {
    return practice;
  },

  setPractice: function(value) {
    practice = value;
    PersistentStore.setPractice(value);
    SessionStore.emitChange();
  },

  getModifier: function() {
    return selectedModifier;
  },

  isModifierSelected: function(modifier) {
    return selectedModifier === modifier;
  },

  selectModifier: function(modifier) {
    selectedModifier = modifier;
    SessionStore.emitChange();
  },

  unselectModifier: function(modifier) {
    selectedModifier = 'natural';
    SessionStore.emitChange();
  },

  clearAllModifiers: function() {
    selectedModifier = 'natural';
    SessionStore.emitChange();
  }
});

let currentPage = SessionStore.PAGES.PAGE_START;

export default SessionStore;
